import {BackendApi, BackendFetch, Host, Processes} from "@hantera/portal-app";
import legacyOrders from "./legacy-orders";
import legacyTagDefinitions from "./legacy-tag-definitions";
import phrases from "./phrases";
import search from "./search";
import legacyStock from "./legacy-stock";
import catalog from "./catalog";
import legacyCustomers from "./legacy-customers";
import user from "./user";
import graph from "./graph";
import orders from "./orders";
import skus from "./skus";
import lookup from "./lookup";
import queries from './queries';

import legacyChannels from "./legacy-channels";
import legacyList from "./legacy-list";
import audit from "./audit";
import iam from "./iam";
import jobs from "./jobs";
import egresses from "./egresses";
import ingresses from "./ingresses";
import traces from "./traces";
import apps from "./apps";

import Decimal from "decimal.js";
import {WebSocketMessage} from "@/portal-websocket-client";
import { HanteraJson } from "@hantera/portal-app";

export default class implements BackendApi {
  
  public legacyTagDefinitions: ReturnType<typeof legacyTagDefinitions>;
  public phrases: ReturnType<typeof phrases>;
  public search: ReturnType<typeof search>;
  public catalog: ReturnType<typeof catalog>;
  public user: ReturnType<typeof user>;
  public graph: ReturnType<typeof graph>;
  public orders: ReturnType<typeof orders>;
  public skus: ReturnType<typeof skus>;
  public lookup: ReturnType<typeof lookup>;
  public queries: ReturnType<typeof queries>;
  
  public legacyCustomers: ReturnType<typeof legacyCustomers>;
  public legacyList: ReturnType<typeof legacyList>;
  public legacyOrders: ReturnType<typeof legacyOrders>;
  public legacyChannels: ReturnType<typeof legacyChannels>;
  public legacyStock: ReturnType<typeof legacyStock>;
  public audit: ReturnType<typeof audit>;
  public iam: ReturnType<typeof iam>;
  public jobs: ReturnType<typeof jobs>;
  public egresses: ReturnType<typeof egresses>;
  public ingresses: ReturnType<typeof ingresses>;
  public traces: ReturnType<typeof traces>;
  public apps: ReturnType<typeof apps>;


  constructor(
    public fetch: BackendFetch,
    host: Host,
    processes: Processes,
    serverUrl: string,
    webSocketSubscribe: (handler: (message: WebSocketMessage) => void) => void
  ) {

    this.phrases = phrases(fetch);
    this.search = search(fetch);
    this.catalog = catalog(fetch);
    this.user = user(fetch, host, processes, webSocketSubscribe);
    this.graph = graph(fetch);
    this.orders = orders(fetch, this.graph);
    this.skus = skus(fetch, this.graph);
    this.lookup = lookup(this.graph);
    this.queries = queries(fetch, processes);
    
    this.legacyCustomers = legacyCustomers(fetch);
    this.legacyStock = legacyStock(fetch);
    this.legacyChannels = legacyChannels(fetch);
    this.legacyList = legacyList(fetch, serverUrl);
    this.legacyOrders = legacyOrders(fetch, processes);
    this.legacyTagDefinitions = legacyTagDefinitions(fetch);
    this.audit = audit(fetch);
    this.iam = iam(fetch);
    this.jobs = jobs(fetch);
    this.egresses = egresses(fetch);
    this.ingresses = ingresses(fetch);
    this.traces = traces(fetch);
    this.apps = apps(fetch);

  }
}

/**
 * Parses JSON body from backend. Applied transforms of dates and numbers automatically into Date and Decimal objects.
 *
 * Delegates the canonical wire-format transforms (number -> Decimal, ISO instant -> Date, ISO date -> DateOnly, graph
 * node unwrapping) to HanteraJson. The legacy `$dec_` and `Utc` quirks are handled here so the canonical module stays
 * symmetric with serialize().
 *
 * @param jsonString The response JSON as string
 * @param isGraphResponse If true, then objects containing an array property named "nodes" will return the array instead
 * of the object, essentially cutting out the object from the response. This is to simplify handling of graph responses.
 */
export function parseResponseJson(jsonString: string, isGraphResponse: boolean = false) {
  const revive = HanteraJson.createReviver({ graph: isGraphResponse });

  return JSON.parse(jsonString,
    (key, value) => {
      const revived = revive(key, value);
      if (revived !== value) {
        return revived;
      }

      // Legacy support:
      if (typeof value === 'string' && value.indexOf('$dec_') === 0) {
        return new Decimal(value.substring(5));
      }

      if (key.endsWith('Utc') && typeof value === 'string') {
        return new Date(value);
      }

      return value;
    }
  );
}


